/**
 * Picker that finds the WorkItem a new Check-In should be linked to.
 *
 *  - `scope="history"` (Return flow) finds the original closed/resolved job,
 *    backed by /api/workitems/history which already scopes results to
 *    historic (resolved/closed) jobs.
 *  - `scope="open"` (Drop-Off flow) finds an open, in-progress job, backed by
 *    /api/workitems?search=.
 *
 * Mobile: tappable button opens a bottom-anchored Drawer with search + list.
 * Desktop: inline MUI Autocomplete.
 *
 * Search terms hit name, phone, email, and ticket number on the server side.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import type { WorkItem } from '@techgitter/contracts';

import { searchWorkItemHistory, searchWorkItems } from '../../services/workItemDataService';

export type JobPickerScope = 'history' | 'open';

export interface JobPickerProps {
  onPick: (workItem: WorkItem) => void;
  /**
   * Which set of jobs to search. `history` (default) searches closed/resolved
   * jobs (Return flow); `open` searches active, in-progress jobs (Drop-Off).
   */
  scope?: JobPickerScope;
}

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 30;
const SEARCH_FIELDS = 'number,customerName,customerPhone,customerEmail';

interface ScopeCopy {
  buttonLabel: string;
  inputLabel: string;
  placeholder: string;
  searchAriaLabel: string;
  noMatch: string;
  emptyPrompt: string;
}

const COPY: Record<JobPickerScope, ScopeCopy> = {
  history: {
    buttonLabel: 'Find original job…',
    inputLabel: 'Find original job',
    placeholder: 'Search by ticket #, name, phone, email…',
    searchAriaLabel: 'Search closed jobs',
    noMatch: 'No closed jobs match. Try a different search term.',
    emptyPrompt: 'Type a ticket number, customer name, phone, or email to find the original job.',
  },
  open: {
    buttonLabel: 'Find in-progress job…',
    inputLabel: 'Find in-progress job',
    placeholder: 'Search by ticket #, name, phone, email…',
    searchAriaLabel: 'Search in-progress jobs',
    noMatch: 'No in-progress jobs match. Try a different search term.',
    emptyPrompt: 'Type a ticket number, customer name, phone, or email to find the in-progress job.',
  },
};

export const JobPicker: React.FC<JobPickerProps> = ({ onPick, scope = 'history' }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));

  const copy = COPY[scope];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Debounced server-side search. Both scopes require a non-empty term —
  // without one we skip the call so we don't return the entire table.
  useEffect(() => {
    const needle = search.trim();
    if (!needle) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    const handle = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(null);
      const run =
        scope === 'open'
          ? searchWorkItems(needle, controller.signal)
          : searchWorkItemHistory(
              { search: needle, searchFields: SEARCH_FIELDS, pageSize: PAGE_SIZE },
              controller.signal,
            ).then(({ items }) => items);
      run
        .then(items => {
          const sorted = [...items].sort((a, b) =>
            (b.resolvedAt ?? b.closedDate ?? b.updatedAt ?? '').localeCompare(
              a.resolvedAt ?? a.closedDate ?? a.updatedAt ?? '',
            ),
          );
          setResults(sorted);
        })
        .catch(err => {
          if (controller.signal.aborted) return;
          console.warn('[JobPicker] search failed:', err);
          setError('Search failed. Try again.');
          setResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search, scope]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const renderSecondary = (w: WorkItem): string => {
    const bits: string[] = [];
    if (w.customerBusinessName) bits.push(w.customerBusinessName);
    if (w.title) bits.push(w.title);
    const when = scope === 'open' ? w.updatedAt : (w.resolvedAt ?? w.closedDate ?? '');
    if (when) bits.push(when.slice(0, 10));
    return bits.join(' · ');
  };

  const renderPrimary = (w: WorkItem): string => {
    const num = w.number != null ? `#${w.number}` : `#${w.id}`;
    const who = w.customerName || 'Unknown customer';
    return `${num} · ${who}`;
  };

  if (isPhone) {
    return (
      <>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setDrawerOpen(true)}
          sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
        >
          {copy.buttonLabel}
        </Button>
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { height: '90dvh', display: 'flex', flexDirection: 'column' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={copy.placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              inputProps={{ 'aria-label': copy.searchAriaLabel }}
            />
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close" sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loading && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {error && !loading && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="error" align="center">{error}</Typography>
              </Box>
            )}
            {!loading && !error && results.length === 0 && search.trim() && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {copy.noMatch}
                </Typography>
              </Box>
            )}
            {!loading && !search.trim() && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {copy.emptyPrompt}
                </Typography>
              </Box>
            )}
            <List dense disablePadding>
              {results.map(w => (
                <ListItemButton
                  key={w.id}
                  onClick={() => {
                    onPick(w);
                    setDrawerOpen(false);
                    setSearch('');
                  }}
                  sx={{ minHeight: 64 }}
                >
                  <ListItemText
                    primary={renderPrimary(w)}
                    secondary={renderSecondary(w)}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Autocomplete<WorkItem>
      options={results}
      getOptionLabel={w => renderPrimary(w)}
      filterOptions={x => x}
      loading={loading}
      inputValue={search}
      onInputChange={(_, value) => setSearch(value)}
      onChange={(_, value) => {
        if (value) {
          onPick(value);
          setSearch('');
          setResults([]);
        }
      }}
      noOptionsText={
        search.trim()
          ? (error ?? copy.noMatch)
          : copy.emptyPrompt
      }
      renderOption={(props, w) => (
        <li {...props} key={w.id}>
          <Box>
            <Typography variant="body2">{renderPrimary(w)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {renderSecondary(w)}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={copy.inputLabel}
          placeholder={copy.placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      blurOnSelect
    />
  );
};
